import React, { useEffect, useState } from 'react';
import { Guid } from '@Shared.Angular/@types/guid';
import IFlowModelForMaintenance from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelForMaintenance';
import { getService } from '@Client/runner.hooks/useService';

const MaintenanceFlowModels: React.FC<{ businessId: Guid | null }> = ({
  businessId
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [flowModel, setFlowModel] = useState<IFlowModelForMaintenance | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);

  const loadFlowModelAfterDelay = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setFlowModel(null);

    if (!businessId || !value) {
      return;
    }
    setLoading(true);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const newTimeoutId = setTimeout(async () => {
      try {
        const model = await getService(
          'maintenanceService'
        ).getFlowModelForMaintenance(businessId, value as unknown as Guid);
        setFlowModel(model || null);
      } catch (err) {
        console.error('Error loading flow model:', err);
        getService('notificationService').showErrorToast(
          'Failed to load flow model.'
        );
      } finally {
        setLoading(false);
      }
    }, 300);
    setTimeoutId(newTimeoutId);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  if (!businessId) {
    return (
      <div className="maintenance-flow-models">
        <p>Please select a business to view flow models.</p>
      </div>
    );
  }

  return (
    <div className="maintenance-flow-models">
      <label>Flow Model Id</label>
      <input value={inputValue} onChange={loadFlowModelAfterDelay} />

      {!loading && flowModel && (
        <div className="flow-model-details">
          <div className="flow-model-fields">
            <div>
              <label>Name:</label>
              <span>{flowModel.name}</span>
            </div>
            <div>
              <label>Active:</label>
              <span>{flowModel.active ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <label>Type:</label>
              <span>{flowModel.type}</span>
            </div>
            {!flowModel.active && (
              <div>
                <label>Deleted By:</label>
                <span>{flowModel.deletedByUserEmail}</span>
              </div>
            )}
          </div>
          {flowModel.flowModelSchemas
            .toSorted((a, b) => a.createdUtc.localeCompare(b.createdUtc))
            .reverse()
            .map((schema) => (
              <div
                key={schema.id}
                className={
                  'flow-model-schema' + (!schema.isObsolete ? ' active' : '')
                }
              >
                <div>
                  <label>Schema Id:</label>
                  <span>{schema.id}</span>
                </div>
                <div>
                  <label>Schema Version:</label>
                  <span>{schema.version}</span>
                </div>
                <div>
                  <label>Is Obsolete:</label>
                  <span>{schema.isObsolete ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <label>Publish Type:</label>
                  <span>{schema.publishType}</span>
                </div>
                <div>
                  <label>Created:</label>
                  <span>
                    {getService('flowinglyMomentService').formatFullDate(
                      schema.createdUtc,
                      'YYYY-MM-DD HH:mm:ss'
                    )}{' '}
                    ({schema.createdUtc.replace('T', ' ')} UTC)
                  </span>
                </div>
                <div>
                  <label>Last Updated:</label>
                  <span>
                    {getService('flowinglyMomentService').formatFullDate(
                      schema.lastUpdatedUtc,
                      'YYYY-MM-DD HH:mm:ss'
                    )}{' '}
                    ({schema.lastUpdatedUtc.replace('T', ' ')} UTC)
                  </span>
                </div>
                {schema.publishRequestedByUserEmail && (
                  <div>
                    <label>Publish Requested By:</label>
                    <span>{schema.publishRequestedByUserEmail}</span>
                  </div>
                )}
                {schema.publishedByUserEmail && (
                  <div>
                    <label>Published By:</label>
                    <span>{schema.publishedByUserEmail}</span>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default MaintenanceFlowModels;
